import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, addDoc, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaYoutube } from 'react-icons/fa';

// Styled Components
const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const VideoItem = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: white;
`;

const VideoThumbnail = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background-color: #000;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const VideoInfo = styled.div`
  padding: 1rem;
`;

const VideoTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #1e3a8a;
`;

const VideoDescription = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const VideoActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  
  &:hover {
    background-color: #f8f9fa;
    transform: translateY(-1px);
  }
  
  &.edit {
    color: #1e3a8a;
  }
  
  &.delete {
    color: #dc3545;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 2rem;
  
  &:hover {
    background-color: #1c3175;
  }
`;

const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Form = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  max-height: 90vh;
  overflow-y: auto;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1e3a8a;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #1e3a8a;
    box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
  }
`;

const HelperText = styled.small`
  display: block;
  margin-top: 0.5rem;
  color: #6c757d;
  font-size: 0.85rem;
`;

const VideoPreview = styled.div`
  margin-top: 1rem;
  padding-top: 56.25%;
  position: relative;
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButton = styled(Button)`
  background-color: #e9ecef;
  color: #495057;
  
  &:hover {
    background-color: #dee2e6;
  }
`;

const SaveButton = styled(Button)`
  background-color: #1e3a8a;
  color: white;
  
  &:hover {
    background-color: #1e40af;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #1e3a8a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background-color: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  color: #6c757d;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #dee2e6;
`;

const VideoTestimonialsManagement = () => {
  const [videos, setVideos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtubeUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Fetch videos from Firebase
  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const videosCollection = collection(db, 'videoTestimonials');
      const videosSnapshot = await getDocs(videosCollection);
      const videosList = videosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVideos(videosList);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setMessage({ text: 'Failed to load video testimonials.', type: 'error' });
    }
    setLoading(false);
  };

  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleAddClick = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      description: '',
      youtubeUrl: ''
    });
    setShowForm(true);
  };

  const handleEditClick = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      youtubeUrl: video.youtubeUrl
    });
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this video testimonial?')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'videoTestimonials', id));
        setMessage({ text: 'Video testimonial deleted successfully!', type: 'success' });
        fetchVideos();
      } catch (error) {
        console.error('Error deleting video:', error);
        setMessage({ text: 'Failed to delete video testimonial.', type: 'error' });
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const youtubeId = extractYouTubeId(formData.youtubeUrl);
      
      if (!youtubeId) {
        setMessage({ text: 'Invalid YouTube URL. Please enter a valid YouTube video URL.', type: 'error' });
        setLoading(false);
        return;
      }
      
      const videoData = {
        title: formData.title,
        description: formData.description,
        youtubeUrl: formData.youtubeUrl,
        youtubeId: youtubeId,
        createdAt: new Date().toISOString()
      };
      
      if (editingVideo) {
        await updateDoc(doc(db, 'videoTestimonials', editingVideo.id), videoData);
        setMessage({ text: 'Video testimonial updated successfully!', type: 'success' });
      } else {
        await addDoc(collection(db, 'videoTestimonials'), videoData);
        setMessage({ text: 'New video testimonial added successfully!', type: 'success' });
      }
      
      setShowForm(false);
      fetchVideos();
    } catch (error) {
      console.error('Error saving video testimonial:', error);
      setMessage({ text: 'Failed to save video testimonial.', type: 'error' });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  const youtubeId = formData.youtubeUrl ? extractYouTubeId(formData.youtubeUrl) : null;

  return (
    <>
      {message.text && (
        <Message type={message.type}>{message.text}</Message>
      )}
      
      <AddButton onClick={handleAddClick}>
        <FaPlus /> Add New Video Testimonial
      </AddButton>
      
      {loading && videos.length === 0 ? (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      ) : videos.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>
            <FaYoutube />
          </EmptyStateIcon>
          <h3>No Video Testimonials Yet</h3>
          <p>Click the button above to add your first video testimonial.</p>
        </EmptyState>
      ) : (
        <VideoGrid>
          {videos.map((video) => (
            <VideoItem key={video.id}>
              <VideoThumbnail>
                <iframe
                  src={`https://www.youtube.com/embed/${video.youtubeId}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoThumbnail>
              <VideoInfo>
                <VideoTitle>{video.title}</VideoTitle>
                <VideoDescription>{video.description}</VideoDescription>
                <VideoActions>
                  <ActionButton className="edit" onClick={() => handleEditClick(video)}>
                    <FaEdit /> Edit
                  </ActionButton>
                  <ActionButton className="delete" onClick={() => handleDeleteClick(video.id)}>
                    <FaTrash /> Delete
                  </ActionButton>
                </VideoActions>
              </VideoInfo>
            </VideoItem>
          ))}
        </VideoGrid>
      )}
      
      {showForm && (
        <FormOverlay>
          <Form onSubmit={handleSubmit}>
            <FormTitle>
              {editingVideo ? 'Edit Video Testimonial' : 'Add New Video Testimonial'}
            </FormTitle>
            
            <FormGroup>
              <Label htmlFor="title">Title *</Label>
              <Input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="e.g., Guest Experience - John Smith"
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="youtubeUrl">YouTube URL *</Label>
              <Input
                type="url"
                id="youtubeUrl"
                name="youtubeUrl"
                value={formData.youtubeUrl}
                onChange={handleInputChange}
                required
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <HelperText>
                Paste the full YouTube URL (e.g., https://www.youtube.com/watch?v=abc123)
              </HelperText>
              
              {youtubeId && (
                <VideoPreview>
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeId}`}
                    title="Video Preview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </VideoPreview>
              )}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="description">Description</Label>
              <TextArea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the testimonial..."
              />
            </FormGroup>
            
            <ButtonGroup>
              <CancelButton type="button" onClick={handleCancel}>
                <FaTimes /> Cancel
              </CancelButton>
              <SaveButton type="submit">
                <FaSave /> Save
              </SaveButton>
            </ButtonGroup>
          </Form>
        </FormOverlay>
      )}
      
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
    </>
  );
};

export default VideoTestimonialsManagement;
